import {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  QueryClient,
  QueryClientProvider,
  useQueryClient,
} from '@tanstack/react-query';
import type { Driver } from 'driver.js';
import { useToast } from '@/contexts/ToastContext';
import { installDemoApi } from './demo-api';
import { findPageTour } from './registry';
import { findTourTarget } from './targets';
import { TourContext } from './tour-context';
import type { PageTour, TourDefinition } from './types';

const SEEN_KEY = 'dc.tours.seen';
/** How long a page may take to show its demo records before the tour gives up. */
const READY_TIMEOUT_MS = 8000;
const READY_POLL_MS = 100;

interface Session {
  tour: PageTour;
  /** The page the tour runs on: a pattern tour's one patient. */
  pathname: string;
  returnTo: string;
  client: QueryClient;
  definition?: TourDefinition;
  uninstall?: () => void;
  driver?: Driver;
}

/**
 * Runs a page's guided tour over demo records.
 *
 * While a tour runs, everything below this provider reads from a throwaway
 * QueryClient instead of the live one, and the demo interceptor answers the
 * tour's own endpoints (see `demo-api.ts`). The subtree is keyed on that swap,
 * so the page remounts once on the way in and once on the way out:
 *
 *  - on the way in it fetches into the empty demo cache and gets demo records;
 *  - on the way out it mounts against the live cache it left, so the real
 *    records are back at once, with no refetch and no demo row left behind.
 *
 * Providers above this one (auth, branch, notifications, chat, realtime) never
 * see the swap and keep running against the real API.
 */
export function TourProvider({ children }: { children: ReactNode }) {
  const liveClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { showError } = useToast();

  const [demoClient, setDemoClient] = useState<QueryClient | null>(null);
  const [seen, setSeen] = useState<string[]>(readSeen);
  const session = useRef<Session | null>(null);

  const tour = findPageTour(location.pathname);

  const finish = useCallback(() => {
    const current = session.current;
    if (!current) return;
    session.current = null;

    current.uninstall?.();
    current.client.clear();
    current.definition?.refreshOnEnd?.forEach((queryKey) =>
      liveClient.invalidateQueries({ queryKey }),
    );

    // One transition for both, because React Router applies `navigate` as
    // one: on its own the swap commits first, and the live page mounts on
    // the tour's clean URL and fetches a list nobody will see.
    startTransition(() => {
      setDemoClient(null);
      // Back to the filters, sort and page the user had open. Only if they
      // are still on the page: a Back press mid-tour already chose where to go.
      if (samePage(window.location.pathname, current.pathname)) {
        navigate(current.returnTo, { replace: true });
      }
    });

    setSeen((prev) => {
      if (prev.includes(current.tour.id)) return prev;
      const next = [...prev, current.tour.id];
      writeSeen(next);
      return next;
    });
  }, [liveClient, navigate]);

  const start = useCallback(async () => {
    if (!tour || session.current) return;

    const client = new QueryClient({
      defaultOptions: {
        queries: {
          retry: false,
          staleTime: Infinity,
          refetchOnWindowFocus: false,
          refetchOnReconnect: false,
        },
      },
    });
    const current: Session = {
      tour,
      pathname: location.pathname,
      returnTo: location.pathname + location.search + location.hash,
      client,
    };
    session.current = current;

    try {
      const [definition, { runTour }] = await Promise.all([
        tour.load(),
        import('./run-tour'),
      ]);
      if (session.current !== current) return;

      current.definition = definition;
      const lang = i18n.language?.startsWith('ar') ? 'ar' : 'en';
      current.uninstall = installDemoApi(definition, lang);
      startTransition(() => {
        setDemoClient(client);
        // A clean query string: the tour explains the default view, and a
        // filter the user had on could hide every row it points at.
        navigate(current.pathname, { replace: true });
      });

      await waitForPage(client, definition.readyTarget);
      if (session.current !== current) return;

      current.driver = runTour({
        steps: definition.steps,
        t,
        rtl: i18n.dir() === 'rtl',
        lang,
        onEnd: finish,
      });
    } catch {
      finish();
      showError(t('tour.couldNotStart'));
    }
  }, [tour, location, i18n, navigate, t, finish, showError]);

  // Leaving the page (Back, a link in a notification) ends the tour.
  useEffect(() => {
    const current = session.current;
    if (!current) return;
    if (samePage(location.pathname, current.pathname)) return;
    if (current.driver?.isActive()) current.driver.destroy();
    else finish();
  }, [location.pathname, finish]);

  // Sign-out or a crash above the routes must not leave the interceptor in.
  useEffect(
    () => () => {
      session.current?.driver?.destroy();
      session.current?.uninstall?.();
    },
    [],
  );

  const value = useMemo(
    () => ({
      available: !!tour,
      running: !!demoClient,
      seen: !tour || seen.includes(tour.id),
      start: () => void start(),
    }),
    [tour, demoClient, seen, start],
  );

  return (
    <TourContext.Provider value={value}>
      <QueryClientProvider
        key={demoClient ? 'tour' : 'live'}
        client={demoClient ?? liveClient}
      >
        {children}
      </QueryClientProvider>
    </TourContext.Provider>
  );
}

/**
 * Resolves once the page shows its demo records: the anchor is on screen and
 * nothing is still loading. Rejects after READY_TIMEOUT_MS.
 */
function waitForPage(client: QueryClient, readyTarget: string) {
  return new Promise<void>((resolve, reject) => {
    const startedAt = Date.now();
    const check = () => {
      // "Something has arrived" as well as "nothing is loading": right after
      // the swap the live page is still painted and the demo cache is empty,
      // which would otherwise pass both other checks.
      const loaded = client
        .getQueryCache()
        .getAll()
        .some((query) => query.state.status === 'success');
      if (loaded && client.isFetching() === 0 && findTourTarget(readyTarget)) {
        // Two frames: the rows that just arrived have to lay out before the
        // first cut-out measures them.
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
        return;
      }
      if (Date.now() - startedAt > READY_TIMEOUT_MS) {
        reject(new Error('Tour page did not become ready'));
        return;
      }
      window.setTimeout(check, READY_POLL_MS);
    };
    window.setTimeout(check, READY_POLL_MS);
  });
}

function samePage(a: string, b: string) {
  return a.replace(/\/+$/, '') === b.replace(/\/+$/, '');
}

function readSeen(): string[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(SEEN_KEY) ?? '[]');
    return Array.isArray(parsed)
      ? parsed.filter((v) => typeof v === 'string')
      : [];
  } catch {
    return [];
  }
}

function writeSeen(ids: string[]) {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify(ids));
  } catch {
    // Private window or blocked storage: the dot just comes back next time.
  }
}
